<script setup lang="ts">
import { computed, markRaw, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { ArrowPathIcon, ArrowsRightLeftIcon, ChevronLeftIcon, InboxIcon, PlusIcon } from '@heroicons/vue/24/outline'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import UserSelect from '@/components/admin/UserSelect.vue'
import {
  usePoleDevStore,
  type PoleDevRequest,
  type PoleDevTask,
  type RequestSource,
  type RequestStatus,
} from '@/stores/usePoleDevStore'
import {
  POLE_DEV_STATUSES,
  POLE_DEV_STATUS_BY_VALUE,
  POLE_DEV_STATUSES_REQUIRING_ESTIMATE,
} from '@/stores/poleDevStatuses'
import { useBoostCoreUsersStore } from '@/stores/useBoostCoreUsersStore'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'

const route = useRoute()
const router = useRouter()
const store = usePoleDevStore()
const usersStore = useBoostCoreUsersStore()
const permStore = useBoostCorePermissionStore()

const SOURCES: { value: RequestSource; label: string }[] = [
  { value: 'DIRECT', label: 'Direct (passage bureau)' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'CHAT', label: 'Chat (Teams)' },
  { value: 'TELEPHONE', label: 'Téléphone' },
  { value: 'TICKET', label: 'Ticket' },
  { value: 'AUTRE', label: 'Autre' },
]

const STATUSES = POLE_DEV_STATUSES
const statusByValue = computed(() => POLE_DEV_STATUS_BY_VALUE)

const selectedStatuses = ref<RequestStatus[]>([
  'BROUILLON',
  'A_CADRER',
  'A_CHIFFRER',
  'CHIFFRE',
  'PRIORISE',
  'PRET_A_PLANIFIER',
  'A_FAIRE',
  'EN_COURS',
  'RECETTE_INTERNE',
  'RECETTE_METIER',
  'PRET_A_DEPLOYER',
])
const search = ref('')
const showCreate = ref(false)
const createTitle = ref('')
const createDescription = ref('')
const createSource = ref<RequestSource>('DIRECT')
const createRequesterUuid = ref<string | null>('')
const createRequestedAt = ref<string>('')
const requestedAtEdit = ref<string>('')

function todayInput(): string {
  return new Date().toISOString().slice(0, 10)
}
function toDateInput(iso?: string): string {
  return iso ? iso.slice(0, 10) : ''
}

const newComment = ref('')
const newReformulation = ref('')
const tasks = ref<PoleDevTask[]>([])

const totalEstimatedHours = computed(() => {
  const values = tasks.value.map((t) => t.estimatedHours).filter((v): v is number => typeof v === 'number' && !isNaN(v))
  if (values.length === 0) return null
  return Math.round(values.reduce((a, b) => a + b, 0) * 100) / 100
})

/**
 * "Est-ce qu'au moins une tâche **sauvegardée** a un chiffrage > 0 ?"
 * On se base sur l'état serveur (`store.request`), pas l'état d'édition local,
 * pour rester aligné avec la validation backend.
 */
const hasSavedEstimate = computed(() => {
  const t = store.request?.reformulation?.tasks ?? []
  return t.some((task) => typeof task.estimatedHours === 'number' && task.estimatedHours > 0)
})

function isStatusBlocked(s: RequestStatus): boolean {
  return POLE_DEV_STATUSES_REQUIRING_ESTIMATE.includes(s) && !hasSavedEstimate.value
}

const GITHUB_ORG = 'DevGroupe-Pandora'
const GITHUB_REPO = 'backlog'

function githubIssueLink(issueNumber: number | undefined): string | null {
  if (!issueNumber) return null
  return `https://github.com/${GITHUB_ORG}/${GITHUB_REPO}/issues/${issueNumber}`
}

function generateTaskId(): string {
  // crypto.randomUUID() est dispo en HTTPS et localhost (Vite dev OK)
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') return crypto.randomUUID()
  return Math.random().toString(36).slice(2) + Date.now().toString(36)
}

function addTask() {
  tasks.value.push({ id: generateTaskId(), title: '', description: '', estimatedHours: undefined })
}
function removeTask(id: string) {
  tasks.value = tasks.value.filter((t) => t.id !== id)
}

const arrowPathIcon = markRaw(ArrowPathIcon)
const chevronLeftIcon = markRaw(ChevronLeftIcon)
const plusIcon = markRaw(PlusIcon)

async function refresh() {
  await store.fetchAll({
    status: selectedStatuses.value,
    search: search.value || undefined,
  })
}

let refreshTimeout: ReturnType<typeof setTimeout> | undefined
function debouncedRefresh() {
  if (refreshTimeout) clearTimeout(refreshTimeout)
  refreshTimeout = setTimeout(refresh, 400)
}

function requesterShort(r: PoleDevRequest): string {
  const rq = r.requester
  return [rq.firstName, rq.lastName].filter(Boolean).join(' ') || rq.email || rq.userUuid
}

function openRequest(id: string) {
  router.push(`/admin/pole-dev/${id}`)
}

function goBack() {
  store.$patch({ request: null })
  router.push('/admin/pole-dev')
}

function openCreate() {
  // Default demandeur = current user
  createRequesterUuid.value = permStore.user?.uuid ?? ''
  createSource.value = 'DIRECT'
  createRequestedAt.value = todayInput()
  showCreate.value = true
}

async function submitCreate() {
  const title = createTitle.value.trim()
  if (!title || !createRequesterUuid.value) return
  const created = await store.create({
    title,
    description: createDescription.value.trim() || undefined,
    source: createSource.value,
    requesterUuid: createRequesterUuid.value,
    requestedAt: createRequestedAt.value || undefined,
  })
  showCreate.value = false
  createTitle.value = ''
  createDescription.value = ''
  if (created?._id) openRequest(created._id)
}

async function saveRequestedAt() {
  if (!store.request) return
  await store.update(store.request._id, { requestedAt: requestedAtEdit.value || undefined })
}

async function submitComment() {
  if (!store.request) return
  const text = newComment.value.trim()
  if (!text) return
  await store.addComment(store.request._id, text)
  newComment.value = ''
}

async function changeStatus(event: Event) {
  if (!store.request) return
  const value = (event.target as HTMLSelectElement).value as RequestStatus
  if (!value || value === store.request.status) return
  await store.changeStatus(store.request._id, value)
}

async function saveDetails() {
  if (!store.request) return
  // Nettoie les tasks : skip celles sans titre.
  const cleanTasks = tasks.value
    .map((t) => ({
      id: t.id,
      title: t.title.trim(),
      description: t.description?.trim() || undefined,
      estimatedHours:
        typeof t.estimatedHours === 'number' && !isNaN(t.estimatedHours) ? t.estimatedHours : undefined,
    }))
    .filter((t) => t.title.length > 0)
  await store.update(store.request._id, {
    reformulation: { text: newReformulation.value, tasks: cleanTasks },
  })
}

async function pushToGithub() {
  if (!store.request) return
  await store.pushToGithub(store.request._id)
}

const syncingGithub = ref(false)
async function syncGithub() {
  if (!store.request) return
  syncingGithub.value = true
  try {
    await store.syncGithub(store.request._id)
  } finally {
    syncingGithub.value = false
  }
}

async function doExport(format: 'csv' | 'xlsx') {
  await store.exportRequests({
    status: selectedStatuses.value,
    search: search.value || undefined,
    format,
  })
}

watch(
  () => route.params.id,
  async (id) => {
    if (id && typeof id === 'string') {
      await store.fetchOne(id)
      if (store.request) {
        newReformulation.value = store.request.reformulation?.text ?? ''
        tasks.value = (store.request.reformulation?.tasks ?? []).map((t) => ({ ...t }))
        requestedAtEdit.value = toDateInput(store.request.requestedAt ?? store.request.createdAt)
      }
    } else {
      store.$patch({ request: null })
      await refresh()
    }
  },
  { immediate: true },
)

watch(search, debouncedRefresh)
watch(selectedStatuses, debouncedRefresh, { deep: true })

onMounted(async () => {
  await refresh()
  if (usersStore.users.length === 0) await usersStore.fetchUsers()
})
</script>

<template>
  <div class="w-full p-6 flex flex-col gap-4">
    <!-- Header liste -->
    <template v-if="!store.request">
      <div class="flex items-center justify-between flex-wrap gap-3">
        <div class="flex items-center gap-3">
          <Bs2Title title="Demandes pôle dev" />
          <span v-if="!store.loading" class="text-sm text-base-content/60 mt-1">{{ store.total }} demande(s)</span>
        </div>
        <div class="flex gap-2">
          <ButtonDaisy text="Recharger" variant="secondary" :icon="arrowPathIcon" :disabled="store.loading" @click="refresh" />
          <ButtonDaisy text="Nouvelle demande" variant="primary" :icon="plusIcon" @click="openCreate" />
          <button class="btn btn-sm" :disabled="store.loading" @click="doExport('csv')">CSV</button>
          <button class="btn btn-sm btn-primary" :disabled="store.loading" @click="doExport('xlsx')">XLSX</button>
        </div>
      </div>
    </template>

    <template v-else>
      <ButtonDaisy text="Retour" variant="primary" :icon="chevronLeftIcon" @click="goBack" />
    </template>

    <!-- Modal création -->
    <div v-if="showCreate" class="modal modal-open">
      <div class="modal-box max-w-xl">
        <h3 class="font-semibold mb-3">Nouvelle demande</h3>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <label class="form-control">
            <span class="label-text text-xs mb-1">Source</span>
            <select v-model="createSource" class="select select-bordered select-sm">
              <option v-for="s in SOURCES" :key="s.value" :value="s.value">{{ s.label }}</option>
            </select>
          </label>
          <label class="form-control">
            <span class="label-text text-xs mb-1">Demandeur (interne)</span>
            <UserSelect v-model="createRequesterUuid" placeholder="Rechercher un demandeur…" />
          </label>
          <label class="form-control">
            <span class="label-text text-xs mb-1">Date de la demande</span>
            <input v-model="createRequestedAt" type="date" class="input input-bordered input-sm w-full" />
          </label>
        </div>

        <label class="form-control mb-3">
          <span class="label-text text-xs mb-1">Titre</span>
          <input v-model="createTitle" class="input input-bordered input-sm w-full" type="text" placeholder="Court mais clair" />
        </label>
        <label class="form-control mb-3">
          <span class="label-text text-xs mb-1">Description (vous pouvez coller un email / message Teams)</span>
          <textarea v-model="createDescription" class="textarea textarea-bordered w-full" rows="8" />
        </label>

        <p class="text-xs text-base-content/60 mb-2">
          La reformulation, les tâches et l'estimation se renseignent dans le détail après création.
        </p>

        <div class="modal-action">
          <button class="btn btn-sm" @click="showCreate = false">Annuler</button>
          <button
            class="btn btn-sm btn-primary"
            :disabled="!createTitle.trim() || !createRequesterUuid || store.loading"
            @click="submitCreate"
          >
            Créer
          </button>
        </div>
      </div>
    </div>

    <!-- Détail -->
    <template v-if="store.request">
      <div class="card bg-base-100 shadow p-4">
        <div class="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div class="text-xs text-base-content/60">
              {{ store.request.code }} ·
              <span class="badge badge-outline badge-xs">{{ store.request.source }}</span>
            </div>
            <h2 class="text-xl font-semibold">{{ store.request.title }}</h2>
            <div class="text-sm text-base-content/70 mt-2">
              Demandeur : <strong>{{ requesterShort(store.request) }}</strong>
            </div>
            <div class="flex items-end gap-2 mt-2">
              <label class="form-control">
                <span class="label-text text-xs mb-1">Date de la demande</span>
                <input v-model="requestedAtEdit" type="date" class="input input-bordered input-sm" />
              </label>
              <button class="btn btn-sm btn-outline" :disabled="store.loading" @click="saveRequestedAt">Enregistrer</button>
            </div>
          </div>
          <div class="text-xs text-right text-base-content/60">
            <div>Demande : {{ store.request.requestedAt ? new Date(store.request.requestedAt).toLocaleDateString('fr-FR') : '—' }}</div>
            <div>Créée : {{ new Date(store.request.createdAt).toLocaleString('fr-FR') }}</div>
            <div>MAJ : {{ new Date(store.request.updatedAt).toLocaleString('fr-FR') }}</div>
          </div>
        </div>
      </div>

      <div class="card bg-base-100 shadow p-4">
        <h3 class="font-semibold mb-3">Statut</h3>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
          <label class="form-control">
            <span class="label-text text-xs mb-1">Statut courant</span>
            <select class="select select-bordered select-sm" :value="store.request.status" @change="changeStatus" :disabled="store.loading">
              <option
                v-for="s in STATUSES"
                :key="s.value"
                :value="s.value"
                :disabled="isStatusBlocked(s.value)"
              >
                {{ s.label }}{{ isStatusBlocked(s.value) ? ' — chiffrage requis' : '' }}
              </option>
            </select>
          </label>
        </div>

        <h4 class="font-semibold text-sm mt-4 mb-2">Historique</h4>
        <ul class="text-xs text-base-content/70 list-disc list-inside space-y-1">
          <li v-for="(h, i) in store.request.statusHistory" :key="i">
            <strong>{{ statusByValue[h.status]?.label ?? h.status }}</strong>
            — {{ new Date(h.at).toLocaleString('fr-FR') }}
            <span v-if="h.firstName || h.lastName">
              par <span class="font-medium">{{ [h.firstName, h.lastName].filter(Boolean).join(' ') }}</span>
            </span>
            <span v-if="h.note" class="italic">— {{ h.note }}</span>
          </li>
        </ul>
      </div>

      <div class="card bg-base-100 shadow p-4">
        <h3 class="font-semibold mb-3">Description originale</h3>
        <div class="text-sm whitespace-pre-wrap text-base-content/80">{{ store.request.description }}</div>
      </div>

      <div class="card bg-base-100 shadow p-4">
        <h3 class="font-semibold mb-3">Reformulation interne</h3>
        <label class="form-control">
          <span class="label-text text-xs mb-1">Texte (cadrage, contexte)</span>
          <textarea v-model="newReformulation" class="textarea textarea-bordered w-full" rows="4" />
        </label>

        <div class="flex items-center justify-between mt-4 mb-2">
          <h4 class="font-semibold text-sm">Tâches</h4>
          <div class="text-sm text-base-content/70">
            Total estimé : <strong>{{ totalEstimatedHours ?? '—' }}</strong>
            <span v-if="totalEstimatedHours != null"> h</span>
          </div>
        </div>

        <div v-if="tasks.length === 0" class="text-sm text-base-content/60 italic mb-2">
          Aucune tâche pour le moment.
        </div>

        <div class="flex flex-col gap-3">
          <div
            v-for="task in tasks"
            :key="task.id"
            class="border border-base-300 rounded p-3 bg-base-50"
          >
            <div class="flex gap-2 items-start">
              <div class="flex-1 flex flex-col gap-2">
                <input
                  v-model="task.title"
                  type="text"
                  placeholder="Titre de la tâche"
                  class="input input-bordered input-sm w-full"
                />
                <textarea
                  v-model="task.description"
                  placeholder="Description / précisions (facultatif)"
                  class="textarea textarea-bordered textarea-sm w-full"
                  rows="2"
                />
              </div>
              <div class="flex flex-col gap-2 w-32">
                <input
                  v-model.number="task.estimatedHours"
                  type="number"
                  min="0"
                  step="0.5"
                  placeholder="h"
                  class="input input-bordered input-sm w-full"
                />
                <button
                  type="button"
                  class="btn btn-xs btn-error btn-outline"
                  :disabled="store.loading"
                  @click="removeTask(task.id)"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>

        <button class="btn btn-sm btn-outline mt-3 self-start" type="button" @click="addTask">
          + Ajouter une tâche
        </button>

        <div class="flex flex-wrap gap-2 mt-4">
          <button class="btn btn-sm btn-primary" :disabled="store.loading" @click="saveDetails">
            Sauvegarder
          </button>
          <template v-if="store.request.githubIssueNumber">
            <a
              :href="githubIssueLink(store.request.githubIssueNumber) ?? '#'"
              target="_blank"
              rel="noopener"
              class="btn btn-sm btn-outline"
            >
              Voir sur GitHub #{{ store.request.githubIssueNumber }}
            </a>
            <button
              class="btn btn-sm btn-outline gap-1"
              :disabled="store.loading"
              title="Resynchroniser statut + estimation côté GitHub"
              @click="syncGithub"
            >
              <ArrowsRightLeftIcon class="w-4 h-4" :class="{ 'animate-spin': syncingGithub }" />
              Sync
            </button>
          </template>
          <button
            v-else
            class="btn btn-sm btn-outline btn-accent"
            :disabled="store.loading"
            @click="pushToGithub"
          >
            Ajouter au backlog GitHub
          </button>
        </div>
      </div>

      <div class="card bg-base-100 shadow p-4">
        <h3 class="font-semibold mb-3">Commentaires internes</h3>
        <div v-if="store.request.comments.length === 0" class="text-sm text-base-content/60">Aucun commentaire.</div>
        <div v-else class="flex flex-col gap-3">
          <div v-for="(c, i) in store.request.comments" :key="i" class="rounded border border-base-300 p-3 bg-base-100">
            <div class="flex justify-between text-xs mb-1">
              <span class="font-semibold">
                {{ [c.firstName, c.lastName].filter(Boolean).join(' ') || c.userUuid }}
              </span>
              <span class="text-base-content/60">{{ new Date(c.createdAt).toLocaleString('fr-FR') }}</span>
            </div>
            <div class="text-sm whitespace-pre-wrap">{{ c.message }}</div>
          </div>
        </div>
        <div class="mt-4 pt-3 border-t flex flex-col gap-2">
          <textarea v-model="newComment" class="textarea textarea-bordered w-full" rows="3" placeholder="Ajouter un commentaire…" />
          <button class="btn btn-sm btn-primary self-end" :disabled="store.loading || !newComment.trim()" @click="submitComment">
            Envoyer
          </button>
        </div>
      </div>

      <details v-if="store.request.metadata && Object.keys(store.request.metadata).length" class="card bg-base-100 shadow p-4">
        <summary class="cursor-pointer text-sm font-semibold">Metadata</summary>
        <pre class="text-xs mt-2 overflow-x-auto">{{ JSON.stringify(store.request.metadata, null, 2) }}</pre>
      </details>
    </template>

    <!-- Liste -->
    <template v-else>
      <div class="card bg-base-100 shadow p-4">
        <div class="flex flex-wrap gap-4 mb-4">
          <label class="form-control flex-1 min-w-[200px]">
            <span class="label-text text-xs mb-1">Rechercher</span>
            <input v-model="search" type="text" placeholder="Code, titre, description…" class="input input-bordered input-sm w-full" />
          </label>

          <div class="form-control">
            <span class="label-text text-xs mb-1">Statuts</span>
            <div class="flex gap-1 flex-wrap">
              <label
                v-for="s in STATUSES"
                :key="s.value"
                class="cursor-pointer flex items-center gap-1 border rounded px-2 py-1 text-xs"
                :class="selectedStatuses.includes(s.value) ? s.color : 'opacity-50'"
              >
                <input type="checkbox" :value="s.value" v-model="selectedStatuses" class="checkbox checkbox-xs" />
                {{ s.label }}
              </label>
            </div>
          </div>
        </div>

        <div v-if="store.loading" class="flex justify-center py-12">
          <ArrowPathIcon class="w-8 h-8 animate-spin text-base-content/40" />
        </div>

        <div v-else-if="store.requests.length === 0" class="flex flex-col items-center py-12 gap-2 text-base-content/60">
          <InboxIcon class="w-12 h-12" />
          <p>Aucune demande</p>
        </div>

        <div v-else class="overflow-x-auto">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Code</th>
                <th>Titre</th>
                <th>Source</th>
                <th>Demandeur</th>
                <th>Statut</th>
                <th>Estim.</th>
                <th>Demande</th>
                <th>Créée</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="r in store.requests" :key="r._id" class="cursor-pointer hover:bg-base-200" @click="openRequest(r._id)">
                <td class="font-mono text-xs">{{ r.code }}</td>
                <td class="max-w-md truncate">{{ r.title }}</td>
                <td><span class="badge badge-outline badge-xs">{{ r.source }}</span></td>
                <td>{{ requesterShort(r) }}</td>
                <td>
                  <span class="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium" :class="statusByValue[r.status]?.color ?? 'bg-base-200'">
                    {{ statusByValue[r.status]?.label ?? r.status }}
                  </span>
                </td>
                <td>{{ r.estimatedHours ?? '—' }}</td>
                <td class="text-xs text-base-content/60">
                  {{ new Date(r.requestedAt ?? r.createdAt).toLocaleDateString('fr-FR') }}
                </td>
                <td class="text-xs text-base-content/60">{{ new Date(r.createdAt).toLocaleDateString('fr-FR') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </template>
  </div>
</template>
